import React from "react"
import {
  Autocomplete,
  FormHelperText,
  FormGroup,
  InputLabel,
  Stack,
  TextField,
  useTheme
} from "@carrotfertility/carotene-core"
import { useOptions, useSetDefaultValue } from "./hooks"
import { calculateInputValue, getMonth } from "./utils"
import { DayValue, MonthValue, YearValue, SelectDropdownProps } from "./DateInputTypes"
import { useIntl } from "react-intl"

let idCounter = 0

/**
 * Known issues:
 * - a warning pops up if disablePortal is enabled, but if disablePortal isnt enabled, then you can't click on select options inside a modal.
 *    - This will either get fixed by the MUI team will go away once we migrate off our other CSS systems
 */

export interface DateInputProps {
  onChange?: (inputValue: string) => void
  numberYearsSupported?: number
  startingYear?: number
  value?: string /* Currently not wired up to control this component */
  id?: string
  name?: string
  helperMessage?: string
  disabled?: boolean
  error?: boolean
  label: string
  required?: boolean
  /**
   * Format should be 'YYYY-MM-DD'
   */
  defaultValue?: string
}

// @ts-expect-error TS7006
function isOptionEqualToValue(option, value): boolean {
  return option.value === value.value
}

function SelectDropdown<T>({ placeholder, error, ...otherProps }: SelectDropdownProps<T>): JSX.Element {
  return (
    <Autocomplete
      fullWidth
      disablePortal
      disableClearable
      isOptionEqualToValue={isOptionEqualToValue}
      openOnFocus
      multiple={false}
      {...otherProps}
      renderInput={(params) => <TextField sx={{ flex: 1 }} placeholder={placeholder} error={error} {...params} />}
    />
  )
}

export const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>((props, ref): JSX.Element => {
  const autoId = React.useMemo(() => `date-input-${idCounter++}`, [])
  const theme = useTheme()
  const [month, setMonth] = React.useState<MonthValue | null>(null)
  const [day, setDay] = React.useState<DayValue | null>(null)
  const [year, setYear] = React.useState<YearValue | null>(null)
  const [inputValue, setInputValue] = React.useState<string>("")
  const emptyHelperMessage = " "
  const intl = useIntl()

  const onChange = props?.onChange

  const { yearOptions, monthOptions, dayOptions } = useOptions({
    month,
    year,
    startingYear: props.startingYear,
    numberYearsSupported: props.numberYearsSupported
  })

  useSetDefaultValue({ defaultValue: props.defaultValue, setMonth, setYear, setDay })

  React.useEffect(() => {
    const inputValue = calculateInputValue({ month, day, year })
    setInputValue(inputValue)
    onChange(inputValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, day, year])

  // @ts-expect-error TS7006
  function onChangeDay(_event, newValue): void {
    setDay(newValue)
  }

  // @ts-expect-error TS7006
  function onChangeMonth(_event, newValue): void {
    if (newValue && day && day.value > getMonth(newValue.value).getDays(year)) {
      setDay(null)
    }
    setMonth(newValue ? { label: newValue?.name, value: newValue?.value } : null)
  }

  // @ts-expect-error TS7006
  function onChangeYear(_event, newValue): void {
    if (newValue && day && month && day.value > getMonth(month.value).getDays(newValue)) {
      setDay(null)
    }
    setYear(newValue)
  }

  return (
    <>
      <FormGroup>
        <InputLabel
          required={props?.required}
          error={props?.error}
          disabled={props?.disabled}
          htmlFor={props?.id || autoId}
        >
          {props.label}
        </InputLabel>
        <Stack spacing={theme.spacing(theme.tokens.spacing.sm)} direction={{ sm: "column", md: "row" }}>
          <SelectDropdown
            error={props?.error}
            disabled={props?.disabled}
            value={month}
            onChange={onChangeMonth}
            placeholder={intl.formatMessage({ defaultMessage: "Mo.", description: "Abbreviation for month" })}
            options={monthOptions}
          />
          <SelectDropdown
            error={props?.error}
            disabled={props?.disabled}
            value={day}
            onChange={onChangeDay}
            placeholder={intl.formatMessage({ defaultMessage: "Day" })}
            options={dayOptions}
          />
          <SelectDropdown
            error={props?.error}
            disabled={props?.disabled}
            value={year}
            onChange={onChangeYear}
            placeholder={intl.formatMessage({ defaultMessage: "Year" })}
            options={yearOptions}
          />
        </Stack>
        <FormHelperText error={props?.error} disabled={props?.disabled}>
          {props.helperMessage || emptyHelperMessage}
        </FormHelperText>
      </FormGroup>
      <input id={props.id || autoId} name={props.name} type="hidden" value={inputValue} ref={ref} />
    </>
  )
})

DateInput.displayName = "DateInput"
DateInput.defaultProps = {
  onChange: () => {
    /* Do nothing */
  },
  startingYear: new Date().getFullYear(),
  numberYearsSupported: 100
}
